/**
 * @file Branding asset upload helpers for app logo and favicon settings.
 */

import {
    getDownloadURL as storageGetDownloadURL,
    ref as storageRef,
    uploadBytesResumable as storageUploadBytesResumable,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-storage.js";

import {
    deleteBrandAssetCallable as configuredDeleteBrandAssetCallable,
    finalizeBrandAssetUploadCallable as configuredFinalizeBrandAssetUploadCallable,
    validateFileUploadCallable as configuredValidateFileUploadCallable,
} from "../../app/firebase/callables.js";
import {
    storage as configuredStorage,
} from "../../app/firebase/firebase.js";

const BRAND_ASSET_KINDS = Object.freeze(["logo", "favicon"]);
const BRAND_ASSET_MIME_TYPES = Object.freeze([
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
]);

function assertBrandAssetKind(kind) {
    if (!BRAND_ASSET_KINDS.includes(kind)) {
        throw new Error("Unsupported brand asset type.");
    }
}

function assertBrandAssetFile(file) {
    const mimeType = String(file?.type || "").toLowerCase();
    if (!BRAND_ASSET_MIME_TYPES.includes(mimeType)) {
        throw new Error("Use a PNG, JPG, or WebP image.");
    }
}

function readImageDimensions(file) {
    return new Promise((resolve) => {
        if (!file || typeof Image === "undefined") {
            resolve(null);
            return;
        }

        const image = new Image();
        const objectUrl = URL.createObjectURL(file);
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve({
                width: image.naturalWidth || null,
                height: image.naturalHeight || null,
            });
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(null);
        };
        image.src = objectUrl;
    });
}

/**
 * Creates branding asset API helpers.
 *
 * @param {object} deps Dependency bag.
 * @returns {object} Branding asset helpers.
 */
export function createBrandAssetsApi({
    deleteBrandAssetCallable,
    finalizeBrandAssetUploadCallable,
    getDownloadURL,
    ref,
    storage,
    uploadBytesResumable,
    validateFileUploadCallable,
}) {
    /**
     * Uploads and finalizes a singleton brand asset.
     *
     * @param {string} kind Asset kind: `logo` or `favicon`.
     * @param {File} file Selected image file.
     * @param {object} [options] Upload options.
     * @param {Function} [options.onProgress] Progress callback.
     * @returns {Promise<object>} Persisted brand asset metadata.
     */
    async function uploadBrandAsset(kind, file, options = {}) {
        assertBrandAssetKind(kind);
        assertBrandAssetFile(file);

        const validationResult = await validateFileUploadCallable({
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            uploadContext: "brandAsset",
        });
        const sanitizedFileName =
            validationResult?.data?.sanitizedFileName || file.name;
        const storagePath = `branding/${kind}/${Date.now()}_${sanitizedFileName}`;
        const fileRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(fileRef, file);
        const dimensions = await readImageDimensions(file);

        await new Promise((resolve, reject) => {
            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    if (typeof options.onProgress !== "function") {return;}
                    const progress = snapshot.totalBytes > 0 ?
                        (snapshot.bytesTransferred / snapshot.totalBytes) * 100 :
                        0;
                    options.onProgress(progress);
                },
                reject,
                resolve,
            );
        });

        const url = await getDownloadURL(uploadTask.snapshot.ref);
        const response = await finalizeBrandAssetUploadCallable({
            kind,
            file: {
                name: sanitizedFileName,
                originalName: file.name,
                url,
                path: storagePath,
                size: file.size,
                type: validationResult?.data?.fileType || file.type,
                width: dimensions?.width || null,
                height: dimensions?.height || null,
            },
        });

        return response?.data?.asset || null;
    }

    /**
     * Deletes a singleton brand asset and clears it from config.
     *
     * @param {string} kind Asset kind: `logo` or `favicon`.
     * @returns {Promise<object>} Delete result.
     */
    async function deleteBrandAsset(kind) {
        assertBrandAssetKind(kind);
        const response = await deleteBrandAssetCallable({kind});
        return response?.data || {success: true};
    }

    return {
        deleteBrandAsset,
        uploadBrandAsset,
    };
}

const configuredBrandAssetsApi = createBrandAssetsApi({
    deleteBrandAssetCallable: configuredDeleteBrandAssetCallable,
    finalizeBrandAssetUploadCallable: configuredFinalizeBrandAssetUploadCallable,
    getDownloadURL: storageGetDownloadURL,
    ref: storageRef,
    storage: configuredStorage,
    uploadBytesResumable: storageUploadBytesResumable,
    validateFileUploadCallable: configuredValidateFileUploadCallable,
});

export const uploadBrandAsset = configuredBrandAssetsApi.uploadBrandAsset;
export const deleteBrandAsset = configuredBrandAssetsApi.deleteBrandAsset;
